import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React from "react";
import {
  DataTable,
  withSelectedEntities,
  CmdButton,
  getTagProps,
  getKeyedTagsAndTagOptions,
  DropdownButton,
  createCommandMenu,
  popoverOverflowModifiers,
  removeDuplicatesIcon,
  useMemoDeepEqual
} from "@teselagen/ui";
import { map, upperFirst, pick, startCase, isFunction } from "lodash-es";
import {
  AnchorButton,
  ButtonGroup,
  Icon,
  Menu,
  Tag,
  Tooltip
} from "@blueprintjs/core";
import { getRangeLength } from "@teselagen/range-utils";
import pluralize from "pluralize";
import { connectToEditor } from "../../withEditorProps";
import { compose } from "recompose";
import commands from "../../commands";
import { sizeSchema } from "./utils";
import { showAddOrEditAnnotationDialog } from "../../GlobalDialogUtils";
import { typeField } from "./typeField";
import { getSequenceWithinRange } from "@teselagen/range-utils";
import { getReverseComplementSequenceString } from "@teselagen/sequence-utils";
const genericAnnotationProperties = ({
  annotationType,
  noType,
  visSubmenu,
  withTags,
  withBases,
  additionalFooterEls
}) => {
  const annotationTypeUpper = upperFirst(annotationType);
  const AnnotationProperties = (props) => {
    const {
      readOnly,
      annotations = {},
      annotationVisibility,
      sequenceLength,
      selectionLayer,
      sequence,
      isProtein,
      allPartTags,
      annotationPropertiesSelectedEntities: _annotationPropertiesSelectedEntities,
      selectedAnnotationId,
      PropertiesProps,
      dispatch,
      editorName
    } = props;
    const instanceRef = React.useRef({ props });
    instanceRef.current.props = props;
    const cmds = React.useMemo(() => commands(instanceRef.current), []);
    const onRowSelect = React.useCallback(
      ([record]) => {
        if (!record) return;
        dispatch({
          type: "SELECTION_LAYER_UPDATE",
          payload: record,
          meta: {
            editorName
          }
        });
      },
      [dispatch, editorName]
    );
    const annotationPropertiesSelectedEntities = _annotationPropertiesSelectedEntities.filter((a) => annotations[a.id]);
    const deleteAnnotation = props[`delete${annotationTypeUpper}`];
    const annotationsToUse = React.useMemo(
      () => map(annotations, (annotation) => {
        return {
          ...annotation,
          ...annotation.strand === void 0 && {
            strand: annotation.forward ? 1 : -1
          },
          size: getRangeLength(annotation, sequenceLength)
        };
      }),
      [annotations, sequenceLength]
    );
    const keyedPartTags = getKeyedTagsAndTagOptions(allPartTags) ?? {};
    const additionalColumns = PropertiesProps?.propertiesList?.find(
      (p) => (p.name || p) === (pluralize(annotationType) || "")
    )?.additionalColumns || [];
    const schema = useMemoDeepEqual(
      () => ({
        fields: [
          {
            path: "name",
            type: "string",
            render: (name, ann) => {
              const checked = !annotationVisibility[`${annotationType}IndividualToHide`][ann.id];
              return /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(
                  Icon,
                  {
                    "data-tip": "Hide/Show",
                    onClick: (e) => {
                      e.stopPropagation();
                      const upperType = startCase(annotationType);
                      if (checked) {
                        props[`hide${upperType}Individual`]([ann.id]);
                      } else {
                        props[`show${upperType}Individual`]([ann.id]);
                      }
                    },
                    style: {
                      cursor: "pointer",
                      marginRight: 4,
                      marginTop: 3,
                      color: "darkgray"
                    },
                    icon: `eye-${checked ? "open" : "off"}`
                  }
                ),
                name
              ] });
            }
          },
          ...!withBases ? [] : [
            {
              path: "bases",
              type: "string",
              render: (bases, primer) => {
                let bps = bases;
                if (!bases) {
                  bps = getSequenceWithinRange(primer, sequence);
                  if (!primer.forward) {
                    bps = getReverseComplementSequenceString(bps);
                  }
                }
                return bps;
              }
            }
          ],
          ...noType ? [] : [
            typeField,
            {
              path: "color",
              type: "string",
              width: 50,
              render: (color) => {
                return /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: { height: 20, width: 20, background: color }
                  }
                );
              }
            }
          ],
          sizeSchema(isProtein),
          ...withTags && allPartTags ? [
            {
              path: "tags",
              type: "string",
              getValueToFilterOn: (o) => {
                const toRet = (o.tags || []).map((tagId) => {
                  const tag = keyedPartTags[tagId];
                  if (!tag) return "";
                  return tag.label;
                }).join(" ");
                return toRet;
              },
              render: (tags) => {
                return /* @__PURE__ */ jsx("div", { style: { display: "flex" }, children: (tags || []).map((tagId, i) => {
                  const tag = keyedPartTags[tagId];
                  if (!tag) return null;
                  return /* @__PURE__ */ jsx(Tag, { ...getTagProps(tag) }, i);
                }) });
              }
            }
          ] : [],
          { path: "strand", type: "number" },
          ...additionalColumns
        ]
      }),
      [
        additionalColumns,
        allPartTags,
        annotationVisibility,
        isProtein,
        keyedPartTags,
        sequence
      ]
    );
    return /* @__PURE__ */ jsx(
      DataTable,
      {
        topLeftItems: getVisFilter(
          createCommandMenu(
            isFunction(visSubmenu) ? visSubmenu(props) : visSubmenu,
            cmds,
            {
              useTicks: true
            }
          )
        ),
        annotationPropertiesSelectedEntities,
        leftOfSearchBarItems: /* @__PURE__ */ jsx(Fragment, { children: !readOnly && /* @__PURE__ */ jsxs(ButtonGroup, { style: { marginTop: 3, marginRight: 4 }, children: [
          /* @__PURE__ */ jsx(
            Tooltip,
            {
              position: "top",
              modifiers: popoverOverflowModifiers,
              content: "New",
              children: /* @__PURE__ */ jsx(
                AnchorButton,
                {
                  disabled: !sequenceLength,
                  icon: "plus",
                  className: "tgNewAnnBtn",
                  onClick: () => {
                    showAddOrEditAnnotationDialog({
                      type: annotationType,
                      annotation: pick(
                        selectionLayer,
                        "start",
                        "end",
                        "forward"
                      )
                    });
                  }
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(
            Tooltip,
            {
              position: "top",
              modifiers: popoverOverflowModifiers,
              content: "Edit",
              children: /* @__PURE__ */ jsx(
                AnchorButton,
                {
                  onClick: () => {
                    showAddOrEditAnnotationDialog({
                      type: annotationType,
                      annotation: annotationPropertiesSelectedEntities[0]
                    });
                  },
                  disabled: annotationPropertiesSelectedEntities.length !== 1,
                  icon: "edit"
                }
              )
            }
          ),
          ["feature"].includes(annotationType) && /* @__PURE__ */ jsx(
            CmdButton,
            {
              text: "",
              icon: "cog",
              "data-tip": "Configure Feature Types",
              cmd: cmds.onConfigureFeatureTypesClick
            }
          ),
          ["part", "primer", "feature"].includes(annotationType) && /* @__PURE__ */ jsx(
            CmdButton,
            {
              text: "",
              icon: removeDuplicatesIcon,
              "data-tip": "Remove Duplicates",
              cmd: cmds[`showRemoveDuplicatesDialog${annotationTypeUpper + "s"}`]
            }
          ),
          additionalFooterEls && additionalFooterEls(props),
          /* @__PURE__ */ jsx(
            Tooltip,
            {
              position: "top",
              modifiers: popoverOverflowModifiers,
              content: "Delete",
              children: /* @__PURE__ */ jsx(
                AnchorButton,
                {
                  onClick: () => {
                    deleteAnnotation(annotationPropertiesSelectedEntities);
                  },
                  className: "tgDeleteAnnsBtn",
                  intent: "danger",
                  disabled: !annotationPropertiesSelectedEntities.length,
                  icon: "trash"
                }
              )
            }
          )
        ] }) }),
        onDoubleClick: (annotation) => {
          showAddOrEditAnnotationDialog({
            type: annotationType,
            annotation
          });
        },
        withCheckboxes: true,
        showFeatureIndividual: props.showFeatureIndividual,
        hideFeatureIndividual: props.hideFeatureIndividual,
        showPartIndividual: props.showPartIndividual,
        hidePartIndividual: props.hidePartIndividual,
        showPrimerIndividual: props.showPrimerIndividual,
        hidePrimerIndividual: props.hidePrimerIndividual,
        annotationVisibility,
        featureLengthsToHide: props.featureLengthsToHide,
        primerLengthsToHide: props.primerLengthsToHide,
        partLengthsToHide: props.partLengthsToHide,
        sequence,
        noPadding: true,
        noFullscreenButton: true,
        onRowSelect,
        selectedIds: selectedAnnotationId,
        formName: "annotationProperties",
        noRouter: true,
        isProtein,
        compact: true,
        isInfinite: true,
        withDisplayOptions: true,
        schema,
        entities: annotationsToUse
      }
    );
  };
  return compose(
    connectToEditor(
      ({
        readOnly,
        annotationVisibility = {},
        sequenceData,
        selectionLayer,
        featureLengthsToHide,
        primerLengthsToHide,
        partLengthsToHide,
        PropertiesProps
      }, ownProps) => {
        return {
          annotationVisibility,
          selectionLayer,
          readOnly,
          featureLengthsToHide,
          primerLengthsToHide,
          partLengthsToHide,
          sequenceData,
          sequence: sequenceData.sequence,
          annotations: sequenceData[annotationType + "s"],
          [annotationType + "s"]: sequenceData[annotationType + "s"],
          sequenceLength: sequenceData.sequence.length,
          PropertiesProps: ownProps.PropertiesProps || PropertiesProps
        };
      }
    ),
    // withEditorProps,
    withSelectedEntities("annotationProperties")
  )(AnnotationProperties);
};
export default genericAnnotationProperties;
export function getVisFilter(submenu) {
  return /* @__PURE__ */ jsx(
    DropdownButton,
    {
      style: { marginTop: 3 },
      icon: "eye-open",
      className: "propertiesVisFilter",
      "data-tip": "Visibility Filter",
      menu: /* @__PURE__ */ jsx(Menu, { children: submenu })
    }
  );
}
